import { BulletinResource } from "@rubi/api-generated";
import { TFunction } from "i18next";

/**
 * How a bulletin's entities read on a **published** bulletin.
 *
 * Deliberately independent of both `add/` and `add-v2/`: the entity list is shown by the detail view,
 * and will be shown by the board card and the generated PDF, none of which belong to a creation flow.
 * Nothing here imports from either, and the vocabulary comes from the `bulletin/entities` namespace
 * rather than the form's own.
 *
 * The wire type is the source of truth — `BulletinResource["entities"]`, so a column added to
 * `alert_bulletin_entity` shows up here as a type error rather than as a silently dropped field.
 */
export type BulletinEntityResource = NonNullable<
  BulletinResource["entities"]
>[number];

/** One stored value, ready to be read as a label and a value. */
export interface EntityField {
  label: string;
  value: string;
}

export interface EntityRow {
  id: string;
  type: "person" | "vehicle" | "item";
  /**
   * "Suspect #1 - John Smith", or just "Suspect #1" for a person with no name yet. An item reads
   * the same way — "Stolen #1 - glove" — and drops to bare "glove" when it has no involvement.
   */
  title: string;
  /** The title without the name, for a row that shows the name in a field of its own. */
  heading: string;
  /** "Person", "Vehicle", "Item / Other" — what the record is, where the heading says what it did. */
  kind: string;
  /** A person's or an item's own name, empty where the heading already is the name. */
  name: string;
  /** Slashed rather than dashed, and only ever a person's. */
  dateOfBirth: string;
  /** What the record stores besides its name. Only a vehicle carries any. */
  fields: EntityField[];
  /** Whatever the officer typed, as they typed it. */
  description: string;
}

/** Only `other` carries free text; every other value is a key with a label. */
const choiceLabel = (
  value: string | undefined,
  other: string | undefined,
  prefix: "colour" | "vehicle_type",
  t: TFunction,
): string => {
  if (!value) return "";
  if (value === "other") return other?.trim() ?? "";
  return t(`${prefix}_${value}`);
};

/** A `make` or `model` is stored as typed, so `other` is the only key to resolve. */
const freeChoice = (value: string | undefined, other: string | undefined) =>
  (value === "other" ? other : value)?.trim() ?? "";

const join = (parts: (string | undefined)[], separator = " ") =>
  parts
    .map((part) => part?.trim())
    .filter((part): part is string => !!part)
    .join(separator);

/**
 * The noun a row is known by: what part the entity plays if the officer said, otherwise its type.
 *
 * An involvement rather than the type, because that is the word the reader is scanning for — a
 * bulletin board full of "Person #1" says nothing, and "Suspect #1" says the thing the bulletin is
 * about. A named item speaks for itself and uses its own name.
 */
const noun = (entity: BulletinEntityResource, t: TFunction): string => {
  if (entity.involvement === "other") {
    return entity.other_involvement?.trim() || t("involvement_other");
  }
  if (entity.involvement) return t(`involvement_${entity.involvement}`);
  if (entity.entity_type === "item" && entity.item_name?.trim()) {
    return entity.item_name.trim();
  }
  return t(entity.entity_type);
};

/**
 * True when `noun` returned the item's own name rather than a type or an involvement — which is
 * the one case a number reads wrong. "Backpack #1" is not what the officer wrote; "Backpack" is.
 * Mirrors the last branch of `noun`, so the two have to change together.
 */
const isNamedItem = (entity: BulletinEntityResource): boolean =>
  entity.entity_type === "item" &&
  !entity.involvement &&
  Boolean(entity.item_name?.trim());

/**
 * What follows the `#n` after a dash: a person's own name, and an item's own name whenever the
 * title had to spend itself on an involvement instead.
 *
 * Without the item branch, "Stolen #1" was the whole of what an officer saw for a row they had
 * typed "glove" into — `noun` returns the involvement before it ever reaches `item_name`, and the
 * item subtitle only carries `additional_description`. `isNamedItem` is the case where the title
 * already **is** the name, which would otherwise read "glove - glove".
 */
const trailingName = (entity: BulletinEntityResource): string => {
  if (entity.entity_type === "person") {
    return join([entity.first_name, entity.last_name]);
  }
  if (entity.entity_type === "item" && !isNamedItem(entity)) {
    return entity.item_name?.trim() ?? "";
  }
  return "";
};

/**
 * "Honda Civic" — what a vehicle is picked out by where there is only room for two words.
 *
 * Exported because the media caption says the same two things over a photo, and the `_other`
 * resolution behind each of them should not be written down twice.
 */
export const vehicleMakeModel = (
  entity: Extract<BulletinEntityResource, { entity_type: "vehicle" }>,
): string =>
  join([
    freeChoice(entity.make, entity.make_other),
    freeChoice(entity.model, entity.model_other),
  ]);

/**
 * `date_of_birth` arrives as `YYYY-MM-DD`, a calendar date with no time and no zone. Reformatting it
 * as a string rather than through `Date` is deliberate: parsing it would place it at UTC midnight
 * and render the day before for every reader west of Greenwich.
 */
export const formatDateOfBirth = (
  dateOfBirth: string | null | undefined,
): string => dateOfBirth?.replace(/-/g, "/") ?? "";

/**
 * The values the record actually stores, each under its own label.
 *
 * Only a vehicle has any: a person is a name and a date of birth, both of which the row says on its
 * own line, and an item is its name — which is why height, build and clothing stay in the
 * description where the officer typed them.
 */
const storedFields = (
  entity: BulletinEntityResource,
  t: TFunction,
): EntityField[] => {
  if (entity.entity_type !== "vehicle") return [];

  return [
    {
      label: t("colour"),
      value: choiceLabel(entity.colour, entity.colour_other, "colour", t),
    },
    { label: t("make"), value: freeChoice(entity.make, entity.make_other) },
    { label: t("model"), value: freeChoice(entity.model, entity.model_other) },
    {
      label: t("type"),
      value: choiceLabel(
        entity.vehicle_type,
        entity.vehicle_type_other,
        "vehicle_type",
        t,
      ),
    },
    { label: t("plate"), value: entity.licence_plate?.trim() ?? "" },
    { label: t("jurisdiction"), value: entity.jurisdiction?.trim() ?? "" },
  ].filter((field) => field.value);
};

/**
 * Numbered **within its own type**, and written `#n` the way the creation form writes it
 * (`entityChipLabel`): the same entity must not be "Suspect #2" while it is being written and
 * "Suspect 1" once it is published. Derived at render time, never stored, so a deleted entity
 * renumbers the rest.
 *
 * `t` is passed in rather than looked up so this stays a pure function: one projection, tested and
 * shared by every view that lists entities.
 */
export const toEntityRows = (
  entities: BulletinEntityResource[],
  t: TFunction,
): EntityRow[] => {
  const seen: Record<string, number> = {};

  return entities.map((entity) => {
    const index = (seen[entity.entity_type] =
      (seen[entity.entity_type] ?? 0) + 1);
    const name = trailingName(entity);

    // The counter still advances for a named item, so the unnamed ones around it keep the numbers
    // the officer saw — only this row's own title drops it.
    const label = noun(entity, t);
    const heading = isNamedItem(entity) ? label : `${label} #${index}`;

    return {
      id: entity.id,
      type: entity.entity_type,
      title: join([heading, name], " - "),
      heading,
      kind: t(entity.entity_type),
      name,
      dateOfBirth:
        entity.entity_type === "person"
          ? formatDateOfBirth(entity.date_of_birth)
          : "",
      fields: storedFields(entity, t),
      description: entity.additional_description?.trim() ?? "",
    };
  });
};
